import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata
} from '../../client-server-common';
import type {WixImageResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {doppeHideableValueCreateHidden} from '../../client-server-common/types/doppe-hideable-value';
import type {RichTextContent, Values} from '@wix/devzai-utils-common';
import {
    arrayRemoveNullValues,
    immerProduce,
    stringTrimPrefix,
    urlGetHostname,
    urlNormalizeHttpUrl
} from '@wix/devzai-utils-common';
import {DoppeActionTypeFollowViewerLang} from './doppe-action-type-follow-viewer.lang';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {doppeActionCtaButtonResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonTypeResolveURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export const DoppeActionTypeFollowPlatforms = {
    Youtube: 'Youtube',
    Twitter: 'Twitter',
    Instagram: 'Instagram',
    Tiktok: 'Tiktok',
    Facebook: 'Facebook',
    Deviantart: 'Deviantart',
    Twitch: 'Twitch',
    Other: 'Other',
} as const;

export type DoppeActionTypeFollowPlatforms = Values<typeof DoppeActionTypeFollowPlatforms>;

export namespace DoppeActionTypeFollow  {
    export interface ActionSettings extends DoppeActionCtaButtonSettings {
        followPageLink: string;
        title: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        images: WixImageResource[];
        profileImage: WixImageResource | null;
        followButtonText: string;
        platform: DoppeActionTypeFollowPlatforms,
        posts: DoppeHideableValue<string>,
        followers: DoppeHideableValue<string>,
        nickName: DoppeHideableValue<string>,
    }
}


export const doppeActionTypeFollow = doppeActionTypeDefineMetadata<DoppeActionTypeFollow.ActionSettings>({
    id: 'io.bymo.action/follow',
    resolveActionSettings: action => {

        const {
            followButtonText = DoppeActionTypeFollowViewerLang.defaultTexts.followButtonText,
            ctaButton,
            ...restSettings
        } = action.settings;

        return {
            followPageLink: '',
            followButtonText: followButtonText,
            followers: doppeHideableValueCreateHidden(DoppeActionTypeFollowViewerLang.defaultTexts.followers),
            posts: doppeHideableValueCreateHidden(DoppeActionTypeFollowViewerLang.defaultTexts.posts),
            images: [],
            profileImage: '',
            nickName: DoppeActionTypeFollowViewerLang.defaultTexts.nickName,
            platform: DoppeActionTypeFollowPlatforms.Instagram,
            title: doppeHideableValueCreateHidden('Title'),
            description: doppeHideableValueCreateHidden('Description'),
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: followButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            followPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            images: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleImages, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            profileImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            followButtonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText),
            posts: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            followers: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            nickName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle)
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.followPageLink = '';
            doppeActionCtaButtonResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...actionSettings.images, actionSettings.profileImage]);
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.profileImage ?? actionSettings.images[0] ?? null;
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonTypeResolveURL(actionSettings.ctaButton.settings, {
            htmlSiteInfo: htmlSiteInfo,
            sourceURL: actionSettings.followPageLink,
            viewerLang: viewerLang,
            showMobileView: false,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.profileImage) {
                actionSettings.profileImage =
                    wixMediaResourceReplaceExternalImage(actionSettings.profileImage, externalImagesUrlToImageResourceMap)
            }

            actionSettings.images = actionSettings.images.map(
                image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
        })
    },
})

export function doppeActionTypeFollowResolvePlatformFromUrl(url: string) {

    const hostname = stringTrimPrefix(urlGetHostname(urlNormalizeHttpUrl(url)), 'www.')

    switch (hostname) {
        case 'tiktok.com':
            return DoppeActionTypeFollowPlatforms.Tiktok;
        case 'facebook.com':
            return DoppeActionTypeFollowPlatforms.Facebook;
        case 'twitter.com':
            return DoppeActionTypeFollowPlatforms.Twitter;
        case 'deviantart.com':
            return DoppeActionTypeFollowPlatforms.Deviantart;
        case 'twitch.tv':
            return DoppeActionTypeFollowPlatforms.Twitch;
        case 'youtube.com':
            return DoppeActionTypeFollowPlatforms.Youtube;
        case 'instagram.com':
            return DoppeActionTypeFollowPlatforms.Instagram;
        default:
            return DoppeActionTypeFollowPlatforms.Other;
    }

}